import { Exclude, Transform } from 'class-transformer';
import {
  IsBoolean,
  IsDate,
  IsNumber,
  IsOptional,
  IsString,
  IsUUID,
  Max,
  Min,
} from 'class-validator';
import { BaseCreateDto, BaseUpdateDto } from '../base.dto';

export class DemoConfigurationSearchJurisdictionalGroupsDto {
  @IsBoolean()
  @Transform(({ obj, key }) => obj[key] === 'true')
  hasDemoConfiguration: boolean;
}

/**
 * DTO for searching for demo configurations.
 */
export class DemoConfigurationSearchDto {
  @IsOptional()
  @IsString()
  jurisdictionalGroupName?: string;

  @IsOptional()
  @IsNumber()
  @Min(0)
  skip?: number;

  @IsNumber()
  @Min(1)
  @Max(20)
  take: number;
}

/**
 * DTO representing a demo configuration.
 */
export interface DemoConfigurationDto {
  id: string;
  jurisdictionalGroupId: string;
  jurisdictionalGroupName: string;
  shouldAutoDeletePatientData: boolean;
  shouldAutoRespondTele: boolean;
}

/**
 * DTO for creating a new demo configuration.
 */
export class CreateDemoConfigurationDto
  extends BaseCreateDto
  implements Partial<DemoConfigurationDto>
{
  @IsUUID()
  jurisdictionalGroupId: string;

  @IsBoolean()
  shouldAutoDeletePatientData: boolean;

  @IsBoolean()
  shouldAutoRespondTele: boolean;
}

/**
 * DTO for updating demo configuration.
 */
export class UpdateDemoConfigurationDto
  extends BaseUpdateDto
  implements Partial<DemoConfigurationDto>
{
  // Demo configuration cannot change jurisdictional group id
  @Exclude()
  jurisdictionalGroupId?: never;

  @IsBoolean()
  shouldAutoDeletePatientData: boolean;

  @IsBoolean()
  shouldAutoRespondTele: boolean;
}

/**
 * DTO for searching for demo patients.
 */
export class DemoPatientsSearchDto {
  @IsOptional()
  @IsString()
  searchText?: string;

  @IsOptional()
  @IsNumber()
  @Min(0)
  skip?: number;

  @IsNumber()
  @Min(1)
  @Max(20)
  take: number;
}

/**
 * DTO representing a demo patient
 */
export interface DemoPatientDto {
  healthRecordId: string;

  demoConfigurationId: string;

  // E.g. ssn or mrn
  patientIdentifier: string;

  patientFirstName: string;

  patientLastName: string;

  jurisdictionalGroupId: string;

  jurisdictionalGroupName: string;

  genderId: string;

  genderName: string;

  birthDate: Date;
}

export class CreateDemoPatientDto
  extends BaseCreateDto
  implements Partial<DemoPatientDto>
{
  @Exclude()
  healthRecordId?: never;

  @Exclude()
  demoConfigurationId?: never;

  @IsUUID()
  jurisdictionalGroupId: string;

  @Exclude()
  jurisdictionalGroupName?: never;

  @IsString()
  patientIdentifier: string;

  @IsString()
  patientFirstName: string;

  @IsString()
  patientLastName: string;

  @IsUUID()
  genderId: string;

  @Exclude()
  genderName?: never;

  @IsDate()
  birthDate: Date;
}

export class UpdateDemoPatientDto
  extends BaseUpdateDto
  implements Partial<DemoPatientDto>
{
  @Exclude()
  healthRecordId?: never;

  @Exclude()
  demoConfigurationId?: never;

  @Exclude()
  jurisdictionalGroupId?: never;

  @Exclude()
  jurisdictionalGroupName?: never;

  @Exclude()
  patientIdentifier?: never;

  @IsString()
  patientFirstName: string;

  @IsString()
  patientLastName: string;

  @Exclude()
  genderId?: never;

  @Exclude()
  genderName?: never;

  @Exclude()
  birthDate?: never;
}
